import * as React from 'react';
import { clsx } from 'clsx';
import { ModalWindow, UIHeading2, UIText1, UIText2 } from '@cian/ui-kit';

import { IDistrictTaRating, TTaTab } from '../../../../types/infrastructure';
import { MAX_TA_RATE, formatTaRate, getTaRateIndex } from '../../../../utils/infrastructure/formatTaRate';
import { DepartmentLink } from '../DepartmentLink';
import { TaRateIcon } from '../TaRateIcon';
import * as styles from './styles.css';

/** Ширина модалки и её z-index — константы map-search. */
const MODAL_WIDTH = 759;
const MODAL_Z_INDEX = 10001;

export interface ITaRatingModalProps {
  rating: IDistrictTaRating;
  tab: TTaTab;
  onClose(): void;
  onSourceClick?(): void;
}

/** Детали оценки Дептранса: бары по характеристикам транспорта или чек-лист инфраструктуры внутри ЖК. */
export function TaRatingModal({ rating, tab, onClose, onSourceClick }: ITaRatingModalProps): React.JSX.Element | null {
  const tabRating = tab === 'inside' ? rating.infrastructure : rating.transport;

  if (!tabRating) {
    return null;
  }

  const content = (
    <div data-testid="district-infrastructure-map-ta-rating-modal">
      <div className={styles['department-link']}>
        <DepartmentLink
          href={rating.sourceUrl}
          size="large"
          testId="district-infrastructure-map-ta-rating-source"
          onClick={onSourceClick}
        />
      </div>
      <div className={clsx(styles['rate-mark-wrapper'], tab === 'inside' && styles['rate-mark-wrapper--inside'])}>
        <div className={styles['rate-mark']}>
          <TaRateIcon tab={tab} />
          <div className={styles['rate-mark-value']}>{formatTaRate(tabRating.rate)}</div>
          <div className={styles['rate-mark-max']}>{`из ${MAX_TA_RATE}`}</div>
        </div>
      </div>
      {tab === 'inside' ? (
        <ul className={styles['checklist']}>
          {rating.infrastructure?.rateDetails.map((detail, index) => (
            <li className={clsx(styles['checklist-item'], detail.done && styles['checklist-item--done'])} key={index}>
              <UIText1 color="current_color">{detail.title}</UIText1>
            </li>
          ))}
        </ul>
      ) : (
        <div className={styles['terms']}>
          {rating.transport?.rateDetails.map((detail, index) => (
            <React.Fragment key={index}>
              <div className={styles['term-label']}>
                <UIText1>{detail.title}</UIText1>
              </div>
              <div className={styles['term-indicator']}>
                <div
                  aria-label={`${detail.title} ${formatTaRate(detail.rate)}`}
                  className={styles['term-bar']}
                  role="img"
                >
                  <div
                    aria-hidden="true"
                    className={styles['term-bar-value']}
                    style={{ width: `${getTaRateIndex(detail.rate) * 100}%` }}
                  />
                </div>
              </div>
              <div className={styles['term-rate']}>
                <UIText2>{formatTaRate(detail.rate)}</UIText2>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <ModalWindow
      escape
      open
      portal
      content={content}
      header={<UIHeading2>{tabRating.title}</UIHeading2>}
      size="M"
      width={MODAL_WIDTH}
      zIndex={MODAL_Z_INDEX}
      onClose={onClose}
    />
  );
}
